import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { InputAdornment, MenuItem, IconButton, styled } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { IdText } from "@/models/apiModels";
import { OrdersFilterRequest } from "@/models/orders";
import { OrderStatus, OrderType } from "@/models/enums";
import BaseTooltip from "@/components/common/baseTooltip";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import BaseInput from "../common/baseInput";
import BaseDateInput from "../common/baseDateInput";
import BaseButton from "../common/baseButton";
import filtersStore from "@/stores/filtersStore";
import SearchProviderAutocomplete from "./search.provider.autocomplete";
import ClearIcon from "@/assets/icons/clear-icon.svg";
import popupService from "@/services/popupService";
import api from "@/services/api";
import to from "await-to-js";

const StyledExcelButton = styled(IconButton)(
    () => `
        &:hover {
          background-color: transparent;
        }
      `
);

interface OrdersFilterProps {
    total: number;
    searchClick: Function;
    filter: OrdersFilterRequest;
    setFilter: Function;
    filterTabs: JSX.Element;
    arrivalDateStart: Dayjs | null;
    arrivalDateEnd: Dayjs | null;
    departureDateStart: Dayjs | null;
    departureDateEnd: Dayjs | null;
    setArrivalDateStart: (date: Dayjs | null) => void;
    setArrivalDateEnd: (date: Dayjs | null) => void;
    setDepartureDateStart: (date: Dayjs | null) => void;
    setDepartureDateEnd: (date: Dayjs | null) => void;
}

function OrdersFilter({
    total,
    searchClick,
    filter,
    setFilter,
    filterTabs,
    arrivalDateStart,
    arrivalDateEnd,
    departureDateStart,
    departureDateEnd,
    setArrivalDateStart,
    setArrivalDateEnd,
    setDepartureDateStart,
    setDepartureDateEnd,
}: OrdersFilterProps) {
    const { t } = useTranslation();
    const [hiddenFilter, setHiddenFilter] = useState(false);

    const handleArrivalDateStart = (date: Dayjs | null) => {
        setArrivalDateStart(date);
        setFilter(() => ({
            ...filter,
            arrivalDateStart: date?.format("DD/MM/YYYY") || null,
        }));
    };

    const handleArrivalDateEnd = (date: Dayjs | null) => {
        setArrivalDateEnd(date);
        setFilter(() => ({
            ...filter,
            arrivalDateEnd: date?.format("DD/MM/YYYY") || null,
        }));
    };

    const handleDepartureDateStart = (date: Dayjs | null) => {
        setDepartureDateStart(date);
        setFilter(() => ({
            ...filter,
            departureDateStart: date?.format("DD/MM/YYYY") || null,
        }));
    };

    const handleDepartureDateEnd = (date: Dayjs | null) => {
        setDepartureDateEnd(date);
        setFilter(() => ({
            ...filter,
            departureDateEnd: date?.format("DD/MM/YYYY") || null,
        }));
    };

    const orderStatusList: IdText[] = (
        Object.values(OrderStatus).filter((val) => isNaN(Number(val))) as (keyof typeof OrderStatus)[]
    ).map((key) => new IdText(OrderStatus[key].toString(), key));

    const orderTypeList: IdText[] = (
        Object.values(OrderType).filter((val) => isNaN(Number(val))) as (keyof typeof OrderType)[]
    ).map((key) => new IdText(OrderType[key].toString(), key));

    const generateOrdersXlsx = async () => {
        popupService.showLoading();
        const [err, resp] = await to(api.exportOrders(filter));
        if (err) {
            popupService.showApiError(err);
            return;
        }

        const response = resp!;
        if (response.data.success && response.data.interfaceId !== 0) {
            popupService.downloadFile(
                t("general.ExportToXLS?"),
                api.generateOrdersExportLink(response.data.interfaceId)
            );
        } else {
            popupService.showError("Unhandled exception");
            return;
        }
    };

    const employeeStatus = [
        {
            value: 1,
            label: t("employee.ActiveStatus"),
        },
        {
            value: 0,
            label: t("employee.InactiveStatus"),
        },
    ];

    const setHiddenFilterData = () => {
        setHiddenFilter(!hiddenFilter);
        filtersStore.setOrdersHiddenFilter(!hiddenFilter);
    };

    function clearFilter() {
        filtersStore.setOrdersHiddenFilter(null);
        setFilter(() => ({
            ...new OrdersFilterRequest(),
        }));
        setArrivalDateStart(null);
        setArrivalDateEnd(null);
        setDepartureDateStart(null);
        setDepartureDateEnd(null);
        searchClick();
    }

    function fetchHiddenFilter() {
        if (filtersStore.ordersHiddenFilter != null) {
            setHiddenFilter(filtersStore.ordersHiddenFilter);
        }
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            fetchHiddenFilter();
        }
        return () => {
            ignore = true;
        };
    }, []);

    const secondFilterRow = hiddenFilter ? (
        <div className="orders-filter-second-row">
            <div className="orders-filter-fields">
                <div>
                    <BaseInput
                        select
                        label={t("orders.orderType")}
                        size="small"
                        value={filter.orderType || ""}
                        onChange={(event) =>
                            setFilter(() => ({
                                ...filter,
                                orderType: event.target.value,
                            }))
                        }
                        sx={{
                            width: "225px",
                            fontSize: 14,
                        }}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    className="select-clear-icon"
                                    sx={{
                                        visibility: filter.orderType ? "visible" : "hidden",
                                    }}
                                    onClick={() =>
                                        setFilter(() => ({
                                            ...filter,
                                            orderType: null,
                                        }))
                                    }
                                >
                                    <img src={ClearIcon} alt="Clear icon" />
                                </IconButton>
                            ),
                        }}
                    >
                        {orderTypeList.map((orderType) => (
                            <MenuItem key={orderType.id} value={orderType.text}>
                                {t(`enums.OrderType.${orderType.text}`)}
                            </MenuItem>
                        ))}
                    </BaseInput>
                </div>
                <div>
                    <BaseInput
                        select
                        label={t("employee.FilterStatus")}
                        size="small"
                        value={filter.employeeStatus ?? ""}
                        onChange={(event) =>
                            setFilter(() => ({
                                ...filter,
                                employeeStatus: event.target.value,
                            }))
                        }
                        sx={{
                            width: "195px",
                        }}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    className="select-clear-icon"
                                    sx={{
                                        visibility:
                                            filter.employeeStatus || filter.employeeStatus?.toString() === "0"
                                                ? "visible"
                                                : "hidden",
                                    }}
                                    onClick={() =>
                                        setFilter(() => ({
                                            ...filter,
                                            employeeStatus: null,
                                        }))
                                    }
                                >
                                    <img src={ClearIcon} alt="Clear icon" />
                                </IconButton>
                            ),
                        }}
                    >
                        {employeeStatus.map((option) => (
                            <MenuItem key={option.label} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </BaseInput>
                </div>
                <div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                            className="date-picker-input"
                            label={t("general.ArrivalStart")}
                            inputFormat="DD/MM/YYYY"
                            value={arrivalDateStart}
                            onChange={handleArrivalDateStart}
                            renderInput={(params) => (
                                <BaseDateInput
                                    {...params}
                                    sx={{
                                        width: "192px",
                                    }}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </div>
                <div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                            className="date-picker-input"
                            label={t("general.ArrivalEnd")}
                            inputFormat="DD/MM/YYYY"
                            value={arrivalDateEnd}
                            onChange={handleArrivalDateEnd}
                            renderInput={(params) => (
                                <BaseDateInput
                                    {...params}
                                    sx={{
                                        width: "192px",
                                    }}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </div>
                <div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                            className="date-picker-input"
                            label={t("general.DepartureStart")}
                            inputFormat="DD/MM/YYYY"
                            value={departureDateStart}
                            onChange={handleDepartureDateStart}
                            renderInput={(params) => (
                                <BaseDateInput
                                    {...params}
                                    sx={{
                                        width: "192px",
                                    }}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </div>
                <div>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DesktopDatePicker
                            className="date-picker-input"
                            label={t("general.DepartureEnd")}
                            inputFormat="DD/MM/YYYY"
                            value={departureDateEnd}
                            onChange={handleDepartureDateEnd}
                            renderInput={(params) => (
                                <BaseDateInput
                                    {...params}
                                    sx={{
                                        width: "192px",
                                    }}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </div>
            </div>
        </div>
    ) : (
        ""
    );

    useEffect(() => {
        let offsetHeight = 139;
        const ordersContainer = document.getElementById("orders-list-grid");
        if (!ordersContainer) {
            return;
        }
        const element = document.getElementById("orders-filter-block");
        if (element) {
            offsetHeight += Number(element.clientHeight);
        }
        ordersContainer.style.height = `calc(100vh - ${offsetHeight}px)`;
    });

    function handleSubmit(event: any) {
        event.preventDefault();
        searchClick();
    }

    return (
        <div id="orders-filter-block" className="orders-filter-block">
            <div className="orders-filter">
                <form onSubmit={handleSubmit}>
                    <div className="font-style orders-span">{t("orders.FilterHeader")}</div>
                    <div className="orders-filter-first-row">
                        <div className="orders-filter-fields">
                            <div>
                                <BaseInput
                                    id="keyword-input"
                                    className="search-input"
                                    placeholder={`${t("orders.FilterSearch")}`}
                                    type="search"
                                    size="small"
                                    value={filter.keywords}
                                    onChange={(event) =>
                                        setFilter(() => ({
                                            ...filter,
                                            keywords: event.target.value,
                                        }))
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        width: "350px",
                                    }}
                                />
                            </div>
                            <div>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopDatePicker
                                        className="date-picker-input"
                                        label={t("orders.fromDate")}
                                        inputFormat="DD/MM/YYYY"
                                        value={filter.fromDate}
                                        onChange={(newValue: Dayjs | null) =>
                                            setFilter(() => ({
                                                ...filter,
                                                fromDate: newValue,
                                            }))
                                        }
                                        renderInput={(params) => (
                                            <BaseDateInput
                                                {...params}
                                                sx={{
                                                    width: "200px",
                                                }}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </div>
                            <div>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopDatePicker
                                        className="date-picker-input"
                                        label={t("orders.endDate")}
                                        inputFormat="DD/MM/YYYY"
                                        value={filter.toDate}
                                        onChange={(newValue: Dayjs | null) =>
                                            setFilter(() => ({
                                                ...filter,
                                                toDate: newValue,
                                            }))
                                        }
                                        renderInput={(params) => (
                                            <BaseDateInput
                                                {...params}
                                                sx={{
                                                    width: "200px",
                                                }}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </div>
                            <div>
                                <BaseInput
                                    select
                                    id="orderStatus-input"
                                    label={t("orders.orderStatus")}
                                    size="small"
                                    value={filter.orderStatus || ""}
                                    onChange={(event) =>
                                        setFilter(() => ({
                                            ...filter,
                                            orderStatus: event.target.value,
                                        }))
                                    }
                                    sx={{
                                        width: "200px",
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <IconButton
                                                className="select-clear-icon"
                                                sx={{
                                                    visibility: filter.orderStatus ? "visible" : "hidden",
                                                }}
                                                onClick={() =>
                                                    setFilter(() => ({
                                                        ...filter,
                                                        orderStatus: null,
                                                    }))
                                                }
                                            >
                                                <img src={ClearIcon} alt="Clear icon" />
                                            </IconButton>
                                        ),
                                    }}
                                >
                                    {orderStatusList?.map((orderStatus) => (
                                        <MenuItem key={orderStatus.id} value={orderStatus.text}>
                                            {orderStatus.text}
                                        </MenuItem>
                                    ))}
                                </BaseInput>
                            </div>
                            <div>
                                <SearchProviderAutocomplete
                                    value={filter.provider}
                                    updateProvider={(args: string | null) => {
                                        setFilter(() => ({
                                            ...filter,
                                            provider: args || null,
                                        }));
                                    }}
                                    width="260px"
                                ></SearchProviderAutocomplete>
                            </div>
                            <div>
                                <BaseTooltip
                                    title={!hiddenFilter ? t("orders.FilterButtonShow") : t("orders.FilterButtonHide")}
                                    placement="bottom"
                                >
                                    <IconButton
                                        className="show-hidden-filter-button-div"
                                        onClick={() => setHiddenFilterData()}
                                        type="button"
                                    >
                                        {hiddenFilter ? (
                                            <KeyboardDoubleArrowUpIcon className="show-hidden-filter-button" />
                                        ) : (
                                            <KeyboardDoubleArrowDownIcon className="show-hidden-filter-button" />
                                        )}
                                    </IconButton>
                                </BaseTooltip>
                            </div>
                        </div>
                        <div className="filter-search-btn">
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                    gap: "15px",
                                }}
                            >
                                <BaseTooltip title={t("employee.import.exportTitle")}>
                                    <StyledExcelButton
                                        type="button"
                                        className="excel-btn"
                                        onClick={() => generateOrdersXlsx()}
                                    >
                                        <i className="icon-f icon-Excel fs-24" />
                                    </StyledExcelButton>
                                </BaseTooltip>
                                <span
                                    onClick={() => clearFilter()}
                                    className="link-element clear-all-btn"
                                    style={{
                                        minWidth: "70px",
                                    }}
                                >
                                    {t("display.ClearAll")}
                                </span>
                            </div>
                            <BaseButton className="basic-button" type="submit" style={{ padding: "0px 30px" }}>
                                {t("general.filter")}
                            </BaseButton>
                        </div>
                    </div>
                    {secondFilterRow}
                </form>
            </div>
            {filterTabs}
        </div>
    );
}

export default OrdersFilter;
